// ¿A quién le sirve la pantalla de "elegí un producto y generá tu primera imagen"?
// — ADR-2026-09-17-el-onboarding-de-tiendanube-es-para-quien-no-genero-nada.
//
// Esa pantalla (`/tiendanube/producto`) existe para UNA cosa: llevar a alguien que
// recién llega hasta su primera imagen. La pregunta que decide si mostrarla no es
// "¿tenés cuenta?" ni "¿ya instalaste la app antes?" —las dos son rodeos— sino la
// directa: **¿esta cuenta ya generó alguna imagen?**
//
// Se eligió así y no por "entró por iniciar sesión" porque esa señal no se puede
// leer con Google (el mismo botón registra e inicia sesión) y se pierde al recargar,
// mientras que ésta sale de la base y vale para cualquier puerta de entrada.
import { supabase } from '../../lib/supabase';

/**
 * ¿Esta cuenta todavía no generó nada, o sea que la pantalla de la primera imagen le
 * sirve?
 *
 * `null` = no se pudo averiguar (sin empresa todavía, o la consulta falló). En ese
 * caso se MUESTRA el onboarding, que es el lado seguro de equivocarse: una pantalla
 * de más la saltea cualquiera con un clic, mientras que saltearle el onboarding a
 * alguien que recién llega lo deja en la app sin saber por dónde empezar.
 */
export function necesitaPrimeraImagen(yaGenero: boolean | null): boolean {
  return yaGenero !== true;
}

/**
 * ¿La empresa tiene al menos una imagen generada DE VERDAD? `null` si no se pudo saber.
 *
 * "De verdad" importa: en `imagenes_generadas` no toda fila es una imagen que el
 * usuario llegó a ver. Los tres estados están documentados en
 * `pages/v2/pollGeneratedByExecutionId.ts` — final mostrable (`active = true`), fallida
 * (`has_error = true`) e intermedia (`active = false` sin error, como el cuadro de más
 * que el backend genera en multi-pose). Se filtra por lo mismo que muestra el historial
 * (`active = true`), más `file_name` presente, porque una fila recién creada todavía no
 * tiene archivo. Medido en producción el 2026-09-17: de 201.106 filas, 40.444 no son
 * mostrables, y 32 empresas tienen filas pero ninguna usable — justo las que fallaron
 * en su primer intento, que son las que MÁS necesitan el onboarding.
 *
 * Una sola fila alcanza: no interesa cuántas hay sino si hay alguna, así que se pide
 * con `limit(1)` y sin contar. La RLS de `imagenes_generadas` ya acota a la empresa
 * del usuario; el `eq` está igual para que la consulta diga lo que busca.
 */
export async function yaGeneroImagenes(
  idCompany: string | null,
): Promise<boolean | null> {
  if (!idCompany) return null;
  try {
    const { data, error } = await supabase
      .from('imagenes_generadas')
      .select('id')
      .eq('id_company', idCompany)
      .eq('active', true)
      .not('has_error', 'is', true)
      .not('file_name', 'is', null)
      .limit(1);
    if (error) return null;
    return (data?.length ?? 0) > 0;
  } catch {
    return null;
  }
}
